/* eslint-disable react/no-array-index-key */
import React, { useCallback, useMemo } from "react";

import { useFormContext, useFormState } from "react-hook-form";

import { Alert, Box, Inline, Text, Tooltip } from "@origin-digital/ods-core";

import { RouterActions, StyledHeading } from "@/components";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";
import { shuffleArray } from "@/utils";

import { softQuote } from "./content.json";
import * as content from "../ServiceProviderSelection/content.json";

import {
  isBatteryJourney,
  isSolarBatteryJourney,
  isSolarJourney,
} from "@/features/SolarForm/utils/formJourney";
import { isSolarGenerationEmpty } from "@/features/SolarForm/utils/solarGenerationValidation";
import { task } from "@origin-digital/reporting-client";
import { ANALYTICS_FORM_NAME, ANALYTICS_TASK_TYPE } from "@/constants";
import { IISelectedProvider } from "@contract/electrify-v1";
import { ServiceProvider } from "@/features/ServiceProviderSelection/ServiceProvider";

export const SoftQuote = ({
  backRoute,
  nextRoute,
  batteryBackRoute,
  solarBatteryBackRoute,
  degradedBatteryBackRoute,
  degradedSolarBackRoute,
  goInstallerBackRoute,
}: {
  backRoute: () => Promise<string>;
  nextRoute: () => Promise<string>;
  batteryBackRoute: () => Promise<string>;
  solarBatteryBackRoute: () => Promise<string>;
  degradedBatteryBackRoute: () => Promise<string>;
  degradedSolarBackRoute: () => Promise<string>;
  goInstallerBackRoute: () => Promise<string>;
}) => {
  const { getValues, setValue } = useFormContext();
  const serviceProviderDetails: ServiceProvider[] = getValues(
    "fetchedServiceProviderDetails"
  );
  const { isValid } = useFieldValidation({
    fields: ["selectedServiceProviders"],
  });
  const {
    errors: { selectedServiceProviders: error },
  } = useFormState({ name: "selectedServiceProviders" });

  const getInstallerHeadingMessage = (text: string) => {
    const installers =
      serviceProviderDetails.length >= 2 ? "installers" : "installer";

    return text.replace(
      "[PLACEHOLDER]",
      `${serviceProviderDetails.length} ${installers}`
    );
  };
  const systemType = getValues("systemType");
  const selectedSystemSize = getValues("selectedSystemSize");

  const trackFormUpdate = async () => {
    const selectedServiceProviders: Array<IISelectedProvider> = getValues(
      "selectedServiceProviders"
    );
    if (await isValid()) {
      task.base(ANALYTICS_TASK_TYPE, {
        action: "update",
        formName: ANALYTICS_FORM_NAME,
        quoteNumber: selectedServiceProviders.length,
        journeyType: getValues("degradedExperience") ? "degraded" : "full",
      });
      return true;
    }
    return false;
  };

  const handleBackRoute = useCallback(() => {
    if (isBatteryJourney(systemType)) {
      return isSolarGenerationEmpty(getValues)
        ? degradedBatteryBackRoute()
        : batteryBackRoute();
    }

    if (getValues("degradedExperience") && !getValues("isKipAndGoInstaller")) {
      return degradedSolarBackRoute();
    }

    if (getValues("degradedExperience") && getValues("isKipAndGoInstaller")) {
      setValue("isKipAndGoInstaller", false);
      setValue("degradedExperience", false);
      return goInstallerBackRoute();
    } else {
      return isSolarBatteryJourney(systemType)
        ? solarBatteryBackRoute()
        : backRoute();
    }
  }, [systemType]);

  const systemSizeIndicator = () => {
    const systemSize = `For an ${selectedSystemSize}kW solar system`;
    return isSolarBatteryJourney(systemType)
      ? `${systemSize} (not including battery costs)`
      : systemSize;
  };

  const isSolarOrSolarBattery =
    isSolarBatteryJourney(systemType) || isSolarJourney(systemType);

  const shuffledProviders = useMemo(
    () => shuffleArray(serviceProviderDetails),
    [serviceProviderDetails]
  );

  return (
    <StyledQuestion>
      <Box paddingX="medium" paddingY="medium">
        <StyledHeading variant="h1" weight="bold" data-id={softQuote.id}>
          {getInstallerHeadingMessage(softQuote.heading)}
        </StyledHeading>
        <Box marginBottom="medium">
          <Text tone="neutralDark">{softQuote.description}</Text>
        </Box>
        {isSolarOrSolarBattery && (
          <>
            <Inline alignY="top" space="small">
              <Text tone="neutralDark" weight="medium">
                {systemSizeIndicator()}
              </Text>
              <Tooltip id="tooltip0" noIconPadding={true}>
                {content.indicativePriceToolTipText}
              </Tooltip>
            </Inline>
          </>
        )}
        {error && (
          <Alert tone="critical">
            <Text>{error.message!.toString()}</Text>
          </Alert>
        )}
        <ServiceProvider serviceProviderDetails={shuffledProviders} />
      </Box>
      <RouterActions
        onBackRoute={handleBackRoute}
        onNextRoute={nextRoute}
        onNext={trackFormUpdate}
      />
    </StyledQuestion>
  );
};
